/**
 * Data hooks for the venue detail-page Workshops tab (BON-168 / BON-197).
 *
 * `useVenueWorkshopStats` loads the KPI-bar aggregates; `useVenueWorkshopsList`
 * loads the "Workshops here" list (workshops at this venue filtered by the
 * `Upcoming | Past | All` toggle, enriched with `paceStatus`/`opFill`/`crew`)
 * via offset paging so the table can "Load more".
 */
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import {
  fetchVenueWorkshopStats,
  fetchWorkshops,
  type VenueWorkshopStats,
  type Workshop,
  type WorkshopDirection,
} from '../../../../api/workshops';

const STALE_60S = 60_000;

/** Page size for the "Load more" workshops list. The list pages via
 *  limit+offset; a full page (=== this size) implies there may be more. */
export const WORKSHOPS_PAGE_SIZE = 10;

export const venueWorkshopStatsKey = (venueId: string) =>
  ['venue', venueId, 'workshop-stats'] as const;
/** Key includes `direction` and the cancelled toggle so switching either is a
 *  FRESH infinite-query (pagination resets, no row bleed between them). */
export const venueWorkshopsListKey = (
  venueId: string,
  direction: WorkshopDirection,
  showCancelled: boolean,
) => ['venue', venueId, 'workshops-list', direction, showCancelled] as const;

export function useVenueWorkshopStats(venueId: string) {
  return useQuery<VenueWorkshopStats>({
    queryKey: venueWorkshopStatsKey(venueId),
    queryFn: () => fetchVenueWorkshopStats({ venueId }),
    enabled: venueId.length > 0,
    staleTime: STALE_60S,
  });
}

/**
 * Offset-paged "Workshops here" list for the given `direction` (BON-197). Each
 * page fetches up to {@link WORKSHOPS_PAGE_SIZE} workshops at this venue;
 * `getNextPageParam` returns the next offset only while the last page came back
 * full (a short page means the list is exhausted). The endpoint returns
 * `Workshop[]` per page.
 *
 * The server owns the past/future boundary (its own `now`), so the client sends
 * only `direction` — no `from = now`. This keeps paging offsets coherent (a
 * client-side per-page `new Date()` would shift the window).
 */
export function useVenueWorkshopsList(
  venueId: string,
  direction: WorkshopDirection,
  showCancelled: boolean,
) {
  return useInfiniteQuery({
    queryKey: venueWorkshopsListKey(venueId, direction, showCancelled),
    queryFn: ({ pageParam }) =>
      fetchWorkshops({
        venueId,
        direction,
        excludeCancelled: !showCancelled,
        limit: WORKSHOPS_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: Workshop[], allPages: Workshop[][]) =>
      lastPage.length === WORKSHOPS_PAGE_SIZE
        ? allPages.reduce((sum, p) => sum + p.length, 0)
        : undefined,
    enabled: venueId.length > 0,
    staleTime: STALE_60S,
  });
}
